import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useSession } from '../../session';
import { Badge, ConfirmDialog, Empty, ErrorBox, Field, Loading, dt, inr, statusTone, useBusy } from '../../ui';

interface Dispute {
  _id: string;
  number: string;
  relationshipId: string;
  subject: { kind: string; id: string };
  disputedAmountPaise: string;
  reason: string;
  status: 'OPEN' | 'RESOLVED';
  raisedBy: { name: string };
  raisedByOrgId: string;
  raisedAt: string;
  messages: { by: { name: string }; at: string; text: string }[];
  resolution?: { outcome: string; note: string; by: { name: string }; at: string; adjustmentId?: string };
}

export function Disputes() {
  const q = useQuery({ queryKey: ['disputes'], queryFn: () => api.get<Dispute[]>('/api/disputes') });
  return (
    <div className="card">
      <h1>Disputes</h1>
      <p className="muted">A dispute keeps the original record and the amount stays due. Any money change is made as a separate correction.</p>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !q.data.length ? <Empty>No disputes.</Empty> : null}
      <ul className="list">
        {q.data?.map((d) => (
          <li key={d._id}>
            <Link className="item" to={`/disputes/${d._id}`}>
              <div className="row between"><strong>{d.number} · {inr(d.disputedAmountPaise)}</strong><Badge tone={statusTone(d.status)}>{d.status.toLowerCase()}</Badge></div>
              <div>{d.reason}</div>
              <small className="muted">by {d.raisedBy.name} · {dt(d.raisedAt)}</small>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DisputeDetail() {
  const { id } = useParams();
  const { active, can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dispute', id], queryFn: () => api.get<{ dispute: Dispute; adjustments: { _id: string; number: string; direction: string; amountPaise: string; status: string }[] }>(`/api/disputes/${id}`) });
  const [text, setText] = useState('');
  const [dlg, setDlg] = useState<'ADJUSTED' | 'NO_CHANGE' | 'WITHDRAWN' | null>(null);
  const [note, setNote] = useState('');
  const [adj, setAdj] = useState('');
  const { busy, error, run } = useBusy();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { dispute: d, adjustments } = q.data!;
  const refresh = () => qc.invalidateQueries({ queryKey: ['dispute', id] });
  const mine = d.raisedByOrgId === active?.orgId;
  return (
    <div className="stack">
      <div className="card">
        <div className="row between"><h1>Dispute {d.number}</h1><Badge tone={statusTone(d.status)}>{d.status.toLowerCase()}</Badge></div>
        <p><strong>{inr(d.disputedAmountPaise)}</strong> on a {d.subject.kind.toLowerCase().replace('_', ' ')}</p>
        <p>{d.reason}</p>
        <small className="muted">Raised by {d.raisedBy.name} · {dt(d.raisedAt)}</small>
        {d.resolution ? <div className="alert ok">Resolved ({d.resolution.outcome.toLowerCase().replace('_', ' ')}) by {d.resolution.by.name}: {d.resolution.note}</div> : null}
        {d.status === 'OPEN' ? (
          <div className="row">
            {can('supplier.disputes.handle') && !mine ? <><button className="btn primary" onClick={() => setDlg('ADJUSTED')}>Resolve with correction</button><button className="btn" onClick={() => setDlg('NO_CHANGE')}>Resolve – no change</button></> : null}
            {can('supplier.disputes.handle') && mine ? <button className="btn" onClick={() => setDlg('NO_CHANGE')}>Resolve</button> : null}
            {mine ? <button className="btn" onClick={() => setDlg('WITHDRAWN')}>Withdraw</button> : null}
            {can('supplier.adjustments.propose') ? <Link className="btn ghost" to={`/adjustments?relationshipId=${d.relationshipId}&disputeId=${d._id}`}>Make a correction</Link> : null}
          </div>
        ) : null}
      </div>
      <div className="card">
        <h2>Messages</h2>
        <ul className="list">{d.messages.map((m, i) => <li key={i}><strong>{m.by.name}</strong>: {m.text} <small className="muted">· {dt(m.at)}</small></li>)}</ul>
        {d.status === 'OPEN' ? (
          <form className="row" onSubmit={(e) => { e.preventDefault(); void run(async () => { await api.post(`/api/disputes/${d._id}/comment`, { text }); setText(''); await refresh(); }); }}>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message" style={{ flex: 1 }} />
            <button className="btn" disabled={!text.trim() || busy}>Send</button>
          </form>
        ) : null}
      </div>
      <ConfirmDialog open={dlg !== null} title="Close this dispute?" lines={[['Dispute', d.number], ['Outcome', dlg?.toLowerCase().replace('_', ' ') ?? '']]} confirmLabel="Close dispute" busy={busy} error={error} onClose={() => setDlg(null)} onConfirm={() => void run(async () => { await api.post(`/api/disputes/${d._id}/resolve`, { outcome: dlg, note, adjustmentId: dlg === 'ADJUSTED' ? adj : undefined }); setDlg(null); await refresh(); })}>
        {dlg === 'ADJUSTED' ? (
          <Field label="Correction">
            <select value={adj} onChange={(e) => setAdj(e.target.value)}>
              <option value="">Choose…</option>
              {adjustments.map((a) => <option key={a._id} value={a._id}>{a.number} · {a.direction === 'CREDIT' ? 'reduction' : 'extra'} {inr(a.amountPaise)} ({a.status.toLowerCase()})</option>)}
            </select>
          </Field>
        ) : null}
        <Field label="Note"><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </ConfirmDialog>
    </div>
  );
}
